/**
 * Copyright © Amazon.com and Affiliates: This deliverable is considered Developed Content as defined in the AWS Service Terms
 * and the SOW between the parties dated 10/31/2024.
 */
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
// import { USER_ID_DEV } from '../../../mock/user';
import { SafeResourceUrl } from '@angular/platform-browser';
import { isDevelopmentEnvironment } from '../../../constants';
import { USER_ID_DEV } from '../../../mock/user';
import { environment } from '../../../../environments/environment';
import { TokenPropertyService } from '../token-property.service';

export type Tile = {
  embedURL: string,
  sanitizedURL?: SafeResourceUrl,
  allProducts: string[],
  productId: string[],
  emailId?: string,
  dashboardId: string,
  sheetId: string,
  visualId: string,
  pinInd?: string,
  likeInd?: string,
  dislikeInd?: string,
  height: number,
  width: number,
  title?: string,
  elementId: string,
  contentId: string,
  // New v2 fields
  score?: number,
  favorited?: boolean,
  pinIndicator?: boolean,
  product?: string[]
}

export type TileV2 = {
  dashboardId: string,
  contentId: string,
  score: number,
  pinIndicator?: boolean,
  product?: string[],
  // Actual API response fields
  title: string,
  pinInd: string,
  likeInd: string,
  dislikeInd: string,
  sheetId: string,
  visualId: string,
  productId: string[], // API returns simple string array like ["STD"]
  persona: string[],   // Also an array based on API response
  height: string,
  width: string
}

export type EmbedUrlResponse = {
  'embedUrl': string
}

@Injectable({
  providedIn: 'root'
})
export class TilesService {
  private API_URL: string;
  private tokenDetails: any;
  constructor(
    private http: HttpClient,
    @Inject('API_URL') apiUrl: string,
    private tokenPropertyService: TokenPropertyService
  ) {
    this.API_URL = apiUrl;

    // Log environment configuration for debugging
    console.log(`[TilesService] Initialized with configuration`, {
      API_URL: this.API_URL,
      quicksightEmbedBaseUrl: environment.quicksightEmbedBaseUrl,
      production: environment.production
    });

    this.tokenDetails = this.tokenPropertyService.getAllProperties('token');
    if (!this.tokenDetails && isDevelopmentEnvironment()) {
      this.tokenDetails = { sub: USER_ID_DEV };
    }
  }

  // New method for v2 endpoint with enhanced functionality
  getTilesV2(): Observable<{ activeProducts: string[], tiles: TileV2[] }> {
    return this.http.get<{ activeProducts: string[], tiles: TileV2[] }>(`${this.API_URL}/v2/user/tiles`).pipe(
      map(response => {
        if (!response.tiles || !Array.isArray(response.tiles)) {
          console.error('Invalid tiles data format:', response);
          return { activeProducts: [], tiles: [] };
        }
        
        return {
          activeProducts: response.activeProducts || [],
          tiles: response.tiles.map(tile => ({
            ...tile,
            // productId is already a simple string array from API response
            product: tile.productId || [],
            // pinIndicator is true when pinInd is 'Y' (meaning tile is favorited/pinned)
            pinIndicator: tile.pinInd === 'Y'
          }))
        };
      }),
      catchError(this.handleError)
    );
  }

 // Method to get embed URL - always fetches fresh URL from API
  // QuickSight embed URLs are single-use and time-limited, so they must not be cached
  getEmbedUrl(dashboardId: string, contentId: string): Observable<string> {
    const token = sessionStorage.getItem('token');
    const idToken = sessionStorage.getItem('id_token');
    
 
    if (!token || !idToken) {
      console.error(`[TilesService] Authentication tokens not found`, { hasToken: !!token, hasIdToken: !!idToken });
      return throwError(() => new Error('Authentication tokens not found'));
    }
    const headers = new HttpHeaders().set('Authorization', `${token}`).set('id_token', `${idToken}`);
    return this.http.get<EmbedUrlResponse>(`${this.API_URL}/tiles/embed-url?dashboardId=${dashboardId}&contentId=${contentId}`, { headers }).pipe(
      map(response => {
        const embedUrl = response['embedUrl'];
        if (!embedUrl) {
          console.warn(`[TilesService] No embed URL received from API`, { dashboardId, contentId, response });
        }
        return embedUrl;
      }),
      catchError(this.handleError)
    );
  }

  // Method to clear all cached embed URLs from sessionStorage
  clearEmbedUrlCache(): void {
    try {
      const totalKeys = sessionStorage.length;
      const keysToRemove: string[] = [];
      for (let i = 0; i < totalKeys; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('embed_url_')) {
          keysToRemove.push(key);
        }
      }
      console.log(`[TilesService] Clearing embed URL cache`, {
        totalKeys,
        embedUrlKeysFound: keysToRemove.length,
        keysToRemove
      });

      let removedCount = 0;
      let failedCount = 0;

      keysToRemove.forEach((key) => {
        try {
          sessionStorage.removeItem(key);
          removedCount++;
        } catch (error) {
          console.error(`[TilesService] Failed to remove cache key: ${key}`, error);
          failedCount++;
        }
      });

      console.log(`[TilesService] Cache clearing completed`, {
        removedCount,
        failedCount,
        totalAttempted: keysToRemove.length,
      });
    } catch (error) {
      console.error(`[TilesService] Failed to clear embed URL cache`, error);
    }
  }

  private sizeMapping(size: string): number {
    switch (size) {
      case 'XS':
        return 2;
      case 'S':
        return 4;
      case 'M':
        return 6;
      case 'L':
        return 8;
      case 'XL':
        return 10;
      default:
        return 6;
    }
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred';
    
    if (error.status === 0) {
      // A client-side or network error occurred.
      console.error('An error occurred:', error.error);
      errorMessage = 'Network error - please check your connection';
    } else if (error.status === 403) {
      // Authentication/authorization error
      console.error('Authentication error:', error);
      errorMessage = 'Access denied - please check your permissions';
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, body was:`, error.error
      );
      
      // Try to extract a meaningful error message
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.error?.error) {
        errorMessage = error.error.error;
      } else if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else {
        errorMessage = `HTTP ${error.status} error occurred`;
      }
    }
    
    // Return an observable with a user-facing error message.
    return throwError(() => new Error(errorMessage));
  }
}
